// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
//
// Clade-annotation SVG renderer covering all five tree layouts.
//
// The z-split is intentional: translucent fill shapes sit at the BOTTOM of
// the render stack (renderCladeAnnotationShapes) and labels float at the TOP
// (renderCladeAnnotationLabels), so edges and nodes are sandwiched between
// them and are never obscured by fills.

import { el } from "./dom.js";
import { TreeTypes } from "../core/constants.js";
import {
  getLeafDescendants,
  getPolygonBounds,
  buildRadialHullPolygon,
  buildAnnularSectorPath,
  calculateLabelPosition,
  calculateWedgeLabelPosition,
} from "./annotations-geometry.js";

// ---------------------------------------------------------------------------
// Hard defaults (lowest-priority layer)
// ---------------------------------------------------------------------------

const HARD_DEFAULTS = {
  strokeWidth: 2,
  opacity: 0.3,
  textColor: [0, 0, 0, 255],
  fontSize: 16,
  textPosition: "center",
  textRotation: 0,
  visible: true,
};

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/**
 * Return the radial distance for a node.
 * The core may set .dist on circular nodes but not on radial nodes,
 * so we fall back to computing it from x/y coordinates.
 *
 * @param {object} n - Node object.
 * @returns {number}
 */
function radiusOf(n) {
  return Number.isFinite(n.dist) ? n.dist : Math.hypot(n.x, n.y);
}

/**
 * Serialize a [[x,y],…] polygon into an SVG `points` attribute string.
 *
 * @param {number[][]} polygon
 * @returns {string}
 */
function polygonToPoints(polygon) {
  return polygon.map(([x, y]) => `${x},${y}`).join(" ");
}

/**
 * Serialize a [[x,y],…] polygon into an SVG path `d` string (M … L … Z).
 *
 * @param {number[][]} polygon
 * @returns {string}
 */
function polygonToPathD(polygon) {
  if (polygon.length === 0) return "";
  const [head, ...tail] = polygon;
  const moves = tail.map(([x, y]) => `L ${x} ${y}`).join(" ");
  return `M ${head[0]} ${head[1]} ${moves} Z`;
}

// ---------------------------------------------------------------------------
// Colour helpers
//
// Fill alpha is applied as fill-opacity (annotation.opacity * fillColor[3]/255);
// stroke is always opaque (stroke-opacity 1).
// ---------------------------------------------------------------------------

function fillOpacity(annotation) {
  const a = (annotation.fillColor[3] ?? 255) / 255;
  return annotation.opacity * a;
}

function colourRGB(channel) {
  const [r, g, b] = channel;
  return `rgb(${r},${g},${b})`;
}

// ---------------------------------------------------------------------------
// Per-layout geometry calculators
// Each returns { shapeEl, labelPosition, textAngle } or null.
// ---------------------------------------------------------------------------

const PADDING = 5;

function calcRectangular(node, leaves, annotation) {
  const minY = Math.min(...leaves.map(l => l.y));
  const maxY = Math.max(...leaves.map(l => l.y));
  const minX = node.x;
  const maxLeafX = Math.max(...leaves.map(l => l.x));

  let maxX = maxLeafX;
  if (annotation.extendToLabels) {
    if (annotation.labelStartX != null) {
      maxX = annotation.labelStartX + (annotation.labelWidthInTreeCoords || 0);
    } else {
      maxX = maxX + (annotation.labelWidthInTreeCoords || 0);
    }
  }

  const polygon = [
    [minX - PADDING, minY - PADDING],
    [maxX + PADDING, minY - PADDING],
    [maxX + PADDING, maxY + PADDING],
    [minX - PADDING, maxY + PADDING],
  ];

  const labelPosition = calculateLabelPosition(
    polygon,
    annotation.textPosition,
    annotation.fontSize,
  );

  const shapeEl = el("polygon", {
    points: polygonToPoints(polygon),
  });

  return {
    shapeEl,
    labelPosition,
    textAngle: annotation.textRotation,
  };
}

function calcDiagonal(node, leaves, annotation) {
  const minX = Math.min(node.x, ...leaves.map(l => l.x));
  let maxX = Math.max(node.x, ...leaves.map(l => l.x));
  const minY = Math.min(node.y, ...leaves.map(l => l.y));
  const maxY = Math.max(node.y, ...leaves.map(l => l.y));

  if (annotation.extendToLabels) {
    if (annotation.labelStartX != null) {
      maxX = annotation.labelStartX + (annotation.labelWidthInTreeCoords || 0);
    } else {
      maxX = maxX + (annotation.labelWidthInTreeCoords || 0);
    }
  }

  const polygon = [
    [minX - PADDING, minY - PADDING],
    [maxX + PADDING, minY - PADDING],
    [maxX + PADDING, maxY + PADDING],
    [minX - PADDING, maxY + PADDING],
  ];

  const labelPosition = calculateLabelPosition(
    polygon,
    annotation.textPosition,
    annotation.fontSize,
  );

  const shapeEl = el("polygon", {
    points: polygonToPoints(polygon),
  });

  return {
    shapeEl,
    labelPosition,
    textAngle: annotation.textRotation,
  };
}

function calcHierarchical(node, leaves, annotation) {
  const minX = Math.min(...leaves.map(l => l.x));
  const maxX = Math.max(...leaves.map(l => l.x));
  const minY = node.y;
  let maxY = Math.max(...leaves.map(l => l.y));

  if (annotation.extendToLabels) {
    if (annotation.labelStartX != null) {
      maxY = annotation.labelStartX + (annotation.labelWidthInTreeCoords || 0);
    } else {
      maxY = maxY + (annotation.labelWidthInTreeCoords || 0);
    }
  }

  const polygon = [
    [minX - PADDING, minY - PADDING],
    [maxX + PADDING, minY - PADDING],
    [maxX + PADDING, maxY + PADDING],
    [minX - PADDING, maxY + PADDING],
  ];

  const labelPosition = calculateLabelPosition(
    polygon,
    annotation.textPosition,
    annotation.fontSize,
  );

  const shapeEl = el("polygon", {
    points: polygonToPoints(polygon),
  });

  return {
    shapeEl,
    labelPosition,
    textAngle: annotation.textRotation,
  };
}

const ANGLE_PADDING = 0.05; // radians

function calcCircular(node, leaves, annotation) {
  const angles = leaves.map(l => l.angle);
  const minAngle = Math.min(...angles);
  const maxAngle = Math.max(...angles);

  const innerRadius = radiusOf(node);
  let outerRadius = Math.max(...leaves.map(l => radiusOf(l)));

  if (annotation.extendToLabels) {
    if (annotation.labelStartX != null) {
      outerRadius = annotation.labelStartX + (annotation.labelWidthInTreeCoords || 0);
    } else {
      outerRadius = outerRadius + (annotation.labelWidthInTreeCoords || 0);
    }
  }

  const startAngle = minAngle - ANGLE_PADDING;
  const endAngle = maxAngle + ANGLE_PADDING;

  const d = buildAnnularSectorPath(startAngle, endAngle, innerRadius, outerRadius);
  const shapeEl = el("path", { d });

  const labelPosition = calculateWedgeLabelPosition(
    minAngle,
    maxAngle,
    innerRadius,
    outerRadius,
    annotation.textPosition,
    annotation.fontSize,
  );

  // Auto-rotate text to read outward from the centre.
  //   autoTextAngle = midAngle * 180 / π
  //   if midAngle ∈ (π/2, 3π/2] add 180 to keep text upright
  //   if annotation.textRotation is provided use it instead of autoTextAngle
  const midAngle = (minAngle + maxAngle) / 2;
  let autoTextAngle = (midAngle * 180) / Math.PI;
  if (midAngle > Math.PI / 2 && midAngle <= (3 * Math.PI) / 2) {
    autoTextAngle += 180;
  }

  const textAngle =
    annotation.textRotation !== undefined && annotation.textRotation !== null
      ? annotation.textRotation
      : autoTextAngle;

  return {
    shapeEl,
    labelPosition,
    textAngle,
  };
}

function calcRadial(node, leaves, annotation) {
  const angles = leaves.map(l => l.angle);
  const minAngle = Math.min(...angles);
  const maxAngle = Math.max(...angles);

  let outerRadius = Math.max(...leaves.map(l => radiusOf(l)));

  if (annotation.extendToLabels) {
    if (annotation.labelStartX != null) {
      outerRadius = annotation.labelStartX + (annotation.labelWidthInTreeCoords || 0);
    } else {
      outerRadius = outerRadius + (annotation.labelWidthInTreeCoords || 0);
    }
  }

  const polygon = buildRadialHullPolygon(
    node,
    leaves,
    outerRadius,
    annotation.extendToLabels,
  );

  // Radial uses a dense smoothed polygon rendered as a <path>.
  const shapeEl = el("path", { d: polygonToPathD(polygon) });

  // Label positioning: calculateWedgeLabelPosition with
  // innerRadius = Math.hypot(node.x, node.y), outerRadius from leaves.
  const innerRadius = Math.hypot(node.x, node.y);
  const labelPosition = calculateWedgeLabelPosition(
    minAngle,
    maxAngle,
    innerRadius,
    outerRadius,
    annotation.textPosition,
    annotation.fontSize,
  );

  // Auto text angle — same rule as calcCircular above.
  const midAngle = (minAngle + maxAngle) / 2;
  let autoTextAngle = (midAngle * 180) / Math.PI;
  if (midAngle > Math.PI / 2 && midAngle <= (3 * Math.PI) / 2) {
    autoTextAngle += 180;
  }

  const textAngle =
    annotation.textRotation !== undefined && annotation.textRotation !== null
      ? annotation.textRotation
      : autoTextAngle;

  return {
    shapeEl,
    labelPosition,
    textAngle,
  };
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

function getCalculator(treeType) {
  switch (treeType) {
    case TreeTypes.Rectangular:   return calcRectangular;
    case TreeTypes.Diagonal:      return calcDiagonal;
    case TreeTypes.Hierarchical:  return calcHierarchical;
    case TreeTypes.Circular:      return calcCircular;
    case TreeTypes.Radial:        return calcRadial;
    default:                      return calcRectangular;
  }
}

// ---------------------------------------------------------------------------
// Annotation preparation
//
// Merges annotationDefaults + hard defaults, resolves the node, collects
// leaves, and computes geometry.  Returns a ready-to-render descriptor or null.
// ---------------------------------------------------------------------------

function prepareAnnotations(graph, props) {
  if (!props.annotations || props.annotations.length === 0) return null;
  if (!graph || !graph.preorderTraversal) return null;

  // Build a fast id→node lookup from preorderTraversal.
  const nodeById = Object.create(null);
  for (const node of graph.preorderTraversal) {
    nodeById[node.id] = node;
  }

  const calculator = getCalculator(props.type);
  const results = [];

  for (const raw of props.annotations) {
    // Three-layer merge: raw annotation overrides annotationDefaults overrides HARD_DEFAULTS.
    const annotation = Object.assign({}, HARD_DEFAULTS, props.annotationDefaults, raw);

    if (!annotation.visible) continue;

    const node = nodeById[annotation.nodeId];
    if (!node || node.isHidden) continue;

    const leaves = getLeafDescendants(node);
    if (leaves.length === 0) continue;

    // Pass label extension info into the annotation object.
    const annotationWithExtent = {
      ...annotation,
      labelStartX: annotation.extendToLabels ? (props.labelStartX ?? null) : null,
      labelWidthInTreeCoords: annotation.extendToLabels
        ? (props.labelWidthInTreeCoords ?? 0)
        : (annotation.labelExtension ?? 0),
    };

    const geom = calculator(node, leaves, annotationWithExtent);
    if (!geom) continue;

    results.push({ annotation, node, geom });
  }

  return results.length > 0 ? results : null;
}

// ---------------------------------------------------------------------------
// Public exports
// ---------------------------------------------------------------------------

/**
 * Render the translucent fill shapes for each visible clade annotation.
 * These should be mounted at the BOTTOM of the SVG layer stack.
 *
 * @param {object} graph - Graph from buildGraph().
 * @param {object} props - Merged props (includes .annotations, .annotationDefaults, .treeType).
 * @returns {SVGGElement|null}
 */
export function renderCladeAnnotationShapes(graph, props) {
  const prepared = prepareAnnotations(graph, props);
  if (!prepared) return null;

  const shapeEls = [];

  for (const { annotation, node, geom } of prepared) {
    const { shapeEl } = geom;

    shapeEl.setAttribute("fill", colourRGB(annotation.fillColor));
    shapeEl.setAttribute("fill-opacity", fillOpacity(annotation));
    shapeEl.setAttribute("stroke", colourRGB(annotation.strokeColor));
    shapeEl.setAttribute("stroke-opacity", "1");
    shapeEl.setAttribute("stroke-width", annotation.strokeWidth);
    shapeEl.setAttribute("data-node-id", node.id);
    shapeEl.setAttribute("data-annotation-id", annotation.id);

    shapeEls.push(shapeEl);
  }

  if (shapeEls.length === 0) return null;

  return el("g", { class: "clade-annotations" }, shapeEls);
}

/**
 * Render text labels for each visible clade annotation that has a non-empty
 * label string.  These should be mounted at the TOP of the SVG layer stack.
 *
 * @param {object} graph - Graph from buildGraph().
 * @param {object} props - Merged props.
 * @returns {SVGGElement|null}
 */
export function renderCladeAnnotationLabels(graph, props) {
  const prepared = prepareAnnotations(graph, props);
  if (!prepared) return null;

  const fontFamily = (props.fontFamily ?? "monospace").replace(/"/g, "'");
  const textEls = [];

  for (const { annotation, node, geom } of prepared) {
    if (!annotation.label) continue;

    const [lx, ly] = geom.labelPosition;
    const degrees = geom.textAngle || 0;

    // textColor may have alpha < 255; emit rgba() in that case.
    const [r, g, b, a = 255] = annotation.textColor;
    const fillStr = a < 255 ? `rgba(${r},${g},${b},${(a / 255).toFixed(4)})` : `rgb(${r},${g},${b})`;

    const textEl = el(
      "text",
      {
        x: lx,
        y: ly,
        "text-anchor": "middle",
        "dominant-baseline": "middle",
        transform: `rotate(${degrees},${lx},${ly})`,
        fill: fillStr,
        "font-size": annotation.fontSize,
        "data-node-id": node.id,
        "data-annotation-id": annotation.id,
      },
      [annotation.label],
    );

    textEls.push(textEl);
  }

  if (textEls.length === 0) return null;

  return el(
    "g",
    {
      class: "clade-annotation-labels",
      "font-family": fontFamily,
    },
    textEls,
  );
}
